/**
 * Routes pour le module Learning (Parcours & Activités)
 * Accessible uniquement aux administrateurs (et rôles autorisés)
 */

import { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { lazy } from 'react';

// Pages Learning Paths
const LearningPathsListPage = lazy(
  () => import('@/pages/Learning/Paths/LearningPathsListPage')
);
const LearningPathCreatePage = lazy(
  () => import('@/pages/Learning/Paths/LearningPathCreatePage')
);
const LearningPathEditPage = lazy(
  () => import('@/pages/Learning/Paths/LearningPathEditPage')
);
const LearningPathDetailPage = lazy(
  () => import('@/pages/Learning/Paths/LearningPathDetailPage')
);

// Pages Activities (Admin)
const ActivityList = lazy(() =>
  import('@/pages/Activities').then((module) => ({
    default: module.ActivityList,
  }))
);
const ActivityFormPage = lazy(() => import('@/pages/Activities/Form'));
const ActivityDetail = lazy(() => import('@/pages/Activities/Detail'));

// Pages Activity Types (Admin)
const ActivityTypesListPage = lazy(
  () => import('@/pages/Learning/Types/ActivityTypesListPage')
);
const ActivityTypeCreatePage = lazy(
  () => import('@/pages/Learning/Types/ActivityTypeCreatePage')
);
const ActivityTypeEditPage = lazy(
  () => import('@/pages/Learning/Types/ActivityTypeEditPage')
);
const ActivityTypeDetailPage = lazy(
  () => import('@/pages/Learning/Types/ActivityTypeDetailPage')
);

// Pages Activity Templates (Admin)
const ActivityTemplatesListPage = lazy(
  () => import('@/pages/Learning/Templates/ActivityTemplatesListPage')
);
const ActivityTemplateCreatePage = lazy(
  () => import('@/pages/Learning/Templates/ActivityTemplateCreatePage')
);
const ActivityTemplateEditPage = lazy(
  () => import('@/pages/Learning/Templates/ActivityTemplateEditPage')
);
const ActivityTemplateDetailPage = lazy(
  () => import('@/pages/Learning/Templates/ActivityTemplateDetailPage')
);

// Pages Activity Categories (Admin)
const ActivityCategoriesListPage = lazy(
  () => import('@/pages/Learning/Categories/ActivityCategoriesListPage')
);
const ActivityCategoryCreatePage = lazy(
  () => import('@/pages/Learning/Categories/ActivityCategoryCreatePage')
);
const ActivityCategoryEditPage = lazy(
  () => import('@/pages/Learning/Categories/ActivityCategoryEditPage')
);
const ActivityCategoryDetailPage = lazy(
  () => import('@/pages/Learning/Categories/ActivityCategoryDetailPage')
);

// Pages Exercises
const ExercisesListPage = lazy(
  () => import('@/pages/Activities/Exercice/ExercisesListPage')
);

const GenerateActivityPage = lazy(() => import('@/pages/Activities/Generate'));
const ActivityAssignmentsPage = lazy(
  () => import('@/pages/Activities/Assignement')
);

export const learningRoutes: RouteObject[] = [
  {
    path: 'learning',
    children: [
      // Parcours Pédagogiques
      {
        path: 'paths',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <LearningPathsListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <LearningPathCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <LearningPathDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <LearningPathEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Activités (CRUD admin)
      {
        path: 'activities',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ActivityList />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <ActivityFormPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'generate',
            element: (
              <ProtectedRoute>
                <GenerateActivityPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <ActivityDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <ActivityFormPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':activityId/assignments/add',
            element: (
              <ProtectedRoute>
                <ActivityAssignmentsPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Types d'activités
      {
        path: 'types',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ActivityTypesListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <ActivityTypeCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <ActivityTypeDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <ActivityTypeEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Templates d'activités
      {
        path: 'templates',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ActivityTemplatesListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <ActivityTemplateCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <ActivityTemplateDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <ActivityTemplateEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Activity Categories (Admin)
      {
        path: 'categories',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ActivityCategoriesListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <ActivityCategoryCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <ActivityCategoryDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <ActivityCategoryEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Exercices
      {
        path: 'exercises',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ExercisesListPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
];
